import { useContext, useEffect, useMemo, useState } from 'react'

import {
  BMICardEmbed,
  BMICardEmbedProps,
  BmiForm,
  BmiFormProps,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import {
  CustomStylesType,
  TemplateProvider,
} from '@hhgtech/hhg-components/mantine'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import { ToolContent } from '@hhgtech/hhg-components/together/interfaces/types'
import { format, subYears } from 'date-fns'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatBMIContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import * as BmiFormHelpers from 'components/molecules/bmiForm/index.helpers'
import {
  BMI_SIDIS_KEY,
  addMoreDataBMIAndSendToSidis,
  generateBmiRedirect,
  getBmiAdditionalQNA,
} from 'components/pages/healthTools/bmi/helpers'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { Category, HealthToolPositionType } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { HealthToolCardEmbedProps } from '../types'

type CommonProps = {
  articleCategories?: Category[] | null
  positionType: HealthToolPositionType
} & HealthToolCardEmbedProps

export type BMICardWrapperProps =
  | ({ onlyUseForm: true } & Partial<BmiFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<BMICardEmbedProps> & CommonProps)

export const BMICardWrapper = ({
  isSide,
  articleCategories,
  positionType,
  onlyUseForm,
  ...props
}: BMICardWrapperProps) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const { locale, router } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const bmiLink = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.BMI), [
    getHealthToolLink,
  ])
  const [bmiContent, setBmiContent] = useState<ToolContent>({} as ToolContent)

  const {
    state: { isLoggedIn = false },
  } = useContext(AppContext) || {}
  const currentUrl = useCurrentUrl()

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_BMI_INFO)
      if (typeof res?.data !== 'undefined') {
        setBmiContent(formatBMIContent(res?.data) as ToolContent)
      }
    })()
  }, [])
  const bmiGenerated = BmiFormHelpers.generateBmiRules({ f, locale })
  const bmiUnit = BmiFormHelpers.bmiUnitValidation[locale]
  const onSubmit = async (data: BmiFormHelpers.BmiFormValue) => {
    const dataToSidis = addMoreDataBMIAndSendToSidis(
      data as BmiFormHelpers.BmiFormValue,
      {
        articleCategories,
        positionType,
        isMobile,
        additionalQuestion: getBmiAdditionalQNA(f, data),
      },
    )
    localStorage.setItem(BMI_SIDIS_KEY, JSON.stringify(dataToSidis))

    const urlBase = window.location.origin + bmiLink
    const urlRedirect = generateBmiRedirect(urlBase, data)

    router.push(
      {
        ...router,
        query: {
          ...router.query,
          gender: data?.gender || '',
          birthday: data?.age
            ? format(
                new Date(subYears(new Date(), Number(data.age))),
                'MM-dd-yyyy',
              )
            : '',
        },
      },
      undefined,
      { shallow: true },
    )

    // TODO: don't know this. copy from previous logic
    // Redirect to SSO if user not login
    // Force userId every submit
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(urlRedirect, 'bmi')
    } else {
      router.push(
        getURLwithSSOTracking(urlRedirect, { source: 'bmi' })?.href ||
          urlRedirect,
      )
    }
  }
  if (!bmiLink)
    return (
      <div
        style={{
          height: 500,
        }}
      ></div>
    )

  return (
    <TemplateProvider variant={CustomStylesType.LEADGEN}>
      {onlyUseForm ? (
        <BmiForm
          onSubmit={onSubmit as any}
          bmiGenerated={bmiGenerated as any}
          bmiUnit={bmiUnit}
          formLayout={isMobile || isSide ? 'default' : 'inline'}
          submitProps={{
            dataEventLabel: currentUrl,
          }}
          withAdditionalQuestion
          {...props}
        />
      ) : (
        <BMICardEmbed
          bmiContent={bmiContent}
          bmiGenerated={bmiGenerated as any}
          bmiUnit={bmiUnit}
          bmiLink={bmiLink}
          formProps={
            {
              formLayout: isMobile || isSide ? 'default' : 'inline',
              onSubmit,
              submitProps: {
                dataEventLabel: currentUrl,
              },
              withAdditionalQuestion: true,
            } as any
          }
          {...props}
        />
      )}
    </TemplateProvider>
  )
}
